// next-app/src/lib/api-client.ts
import { isApiMockEnabled } from '@/lib/api-mock-config';
import { mockLogin, mockSignup } from '@/lib/api-mocks';
import { useAuthStore } from '@/store/auth-store';
import type {
  AccountResponse,
  ApiResponse,
  CheckInDataResponse,
  CheckOutDataResponse,
  CheckOutRecord,
  DTGEvent,
  DTGSeason,
  Dealer,
  EligibilityCheckResponse,
  EventPortalLoginResponse,
  EventResponse,
  EventStatsResponse,
  EventTokenResponse,
  EventsListResponse,
  HashToTokenResponse,
  LoginResponse,
  MagicLoginResponse,
  RespondentsResponse,
  SurveyConfigResponse,
  SurveyResponse,
  SurveySubmitResponse,
  TrainingProgressResponse,
  TwoFAResponse,
  VehicleOfInterest,
} from '@/types';
import { API_URL, APP_VERSION } from './api';

// ============================================================================
// Fetch Helpers
// ============================================================================

/**
 * Standard JSON fetch with authorization headers.
 */
async function doFetch<T = unknown>(
  url: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const token = useAuthStore.getState().dealerToken;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'latitude-app-version': `web:${APP_VERSION}`,
    ...options.headers,
  };

  // Add authorization if token exists
  if (token) {
    (headers as Record<string, string>)['Authorization'] = token;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  let result: T | undefined;
  try {
    result = await response.json();
  } catch {
    console.error('[doFetch] Failed to parse JSON response');
  }

  // Handle 403 - unauthorized/expired token
  if (response.status === 403) {
    console.warn('[doFetch] 403 response - logging out');
    useAuthStore.getState().doLogout();
  }

  return { response, result };
}

/**
 * Multipart form data fetch for file uploads.
 */
async function doMultipart<T = unknown>(url: string, formData: FormData): Promise<ApiResponse<T>> {
  const token = useAuthStore.getState().dealerToken;

  const headers: HeadersInit = {
    'latitude-app-version': `web:${APP_VERSION}`,
    // Note: Do NOT set Content-Type for multipart - browser sets it with boundary
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = token;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  });

  let result: T | undefined;
  try {
    result = await response.json();
  } catch {
    console.error('[doMultipart] Failed to parse JSON response');
  }

  if (response.status === 403) {
    useAuthStore.getState().doLogout();
  }

  return { response, result };
}

const DEALER_API_URL = `${API_URL}/dealers/v1`;
const EVENTS_API_URL = `${API_URL}/events/v1`;
const MOBILE_API_URL = `${API_URL}/mobile/v1`;

// ============================================================================
// API Client
// ============================================================================

export const api = {
  // --------------------------------------------------------------------------
  // Season
  // --------------------------------------------------------------------------

  /**
   * Get current season data (public endpoint, no auth required).
   * GET view_data/current_season
   */
  async getCurrentSeason(): Promise<ApiResponse<DTGSeason>> {
    return doFetch<DTGSeason>(`${DEALER_API_URL}/view_data/current_season`, {
      method: 'GET',
    });
  },

  // --------------------------------------------------------------------------
  // Authentication
  // --------------------------------------------------------------------------

  /**
   * Authenticate dealer with email and password.
   * POST v1/auth/signin
   */
  async login(email: string, password: string): Promise<ApiResponse<LoginResponse>> {
    if (isApiMockEnabled()) {
      return mockLogin(email, password);
    }
    return doFetch<LoginResponse>(`${DEALER_API_URL}/auth/signin`, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  /**
   * Authenticate event portal staff with event ID and password.
   * POST /auth/signin
   */
  async eventPortalLogin(
    eventId: string,
    password: string
  ): Promise<ApiResponse<EventPortalLoginResponse>> {
    return doFetch<EventPortalLoginResponse>(`${DEALER_API_URL}/auth/signin`, {
      method: 'POST',
      body: JSON.stringify({ eventId, password }),
    });
  },

  /**
   * Register new dealer account.
   * POST v1/auth/signup
   */
  async signup(dealer: Partial<Dealer>, password: string): Promise<ApiResponse<LoginResponse>> {
    if (isApiMockEnabled()) {
      return mockSignup(dealer);
    }
    return doFetch<LoginResponse>(`${DEALER_API_URL}/auth/signup`, {
      method: 'POST',
      body: JSON.stringify({ ...dealer, password }),
    });
  },

  /**
   * Request password reset email.
   * POST v1/auth/verify_email
   */
  async forgotPassword(contact_email: string): Promise<ApiResponse<{ message: string }>> {
    return doFetch(`${DEALER_API_URL}/auth/verify_email`, {
      method: 'POST',
      body: JSON.stringify({ contact_email }),
    });
  },

  /**
   * Reset password with token from email.
   * POST v1/auth/change_password
   */
  async resetPassword(password: string, token: string): Promise<ApiResponse<{ message: string }>> {
    return doFetch(`${DEALER_API_URL}/auth/change_password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
      headers: { token },
    });
  },

  /**
   * Server-side logout.
   * POST logout
   */
  async logout(): Promise<void> {
    await doFetch(`${DEALER_API_URL}/logout`, { method: 'POST' });
    useAuthStore.getState().doLogout();
  },

  /**
   * Get current dealer account.
   * GET v1/auth/account
   */
  async getAccount(): Promise<ApiResponse<AccountResponse>> {
    return doFetch<AccountResponse>(`${DEALER_API_URL}/auth/account`, {
      method: 'GET',
    });
  },

  // --------------------------------------------------------------------------
  // Dealer
  // --------------------------------------------------------------------------

  /**
   * Get dealer information.
   * GET v1/event/dealer
   */
  async getDealer(): Promise<ApiResponse<{ dealer: Dealer }>> {
    return doFetch(`${DEALER_API_URL}/event/dealer`, { method: 'GET' });
  },

  /**
   * Update dealer profile.
   * POST v1/auth/updateDealer
   */
  async updateDealer(dealer: Dealer): Promise<ApiResponse<{ dealer: Dealer }>> {
    return doFetch(`${DEALER_API_URL}/auth/updateDealer`, {
      method: 'POST',
      body: JSON.stringify(dealer),
    });
  },

  // --------------------------------------------------------------------------
  // Events
  // --------------------------------------------------------------------------

  /**
   * Get all events for current dealer.
   * GET v1/event/list
   */
  async getEvents(): Promise<ApiResponse<EventsListResponse>> {
    return doFetch<EventsListResponse>(`${DEALER_API_URL}/event/list`, {
      method: 'GET',
    });
  },

  /**
   * Get single event request details.
   * GET v1/event/request/{id}
   */
  async getEventRequest(eventRequestId: string | number): Promise<ApiResponse<EventResponse>> {
    return doFetch<EventResponse>(`${DEALER_API_URL}/event/request/${eventRequestId}`, {
      method: 'GET',
    });
  },

  /**
   * Check dealer eligibility for new event.
   * GET v1/event/check-request
   */
  async checkRequest(): Promise<ApiResponse<EligibilityCheckResponse>> {
    return doFetch<EligibilityCheckResponse>(`${DEALER_API_URL}/event/check-request`, {
      method: 'GET',
    });
  },

  /**
   * Create new event request (eligibility form submission).
   * POST v1/event/create-request
   */
  async saveRequest(eventRequest: Partial<DTGEvent>): Promise<ApiResponse<EventResponse>> {
    // Adjust date to noon to avoid timezone issues (matching original behavior)
    const formattedRequest = { ...eventRequest };
    if (formattedRequest.requested_date) {
      const date = new Date(formattedRequest.requested_date);
      date.setHours(12, 0, 0, 0);
      formattedRequest.requested_date = date;
    }

    return doFetch<EventResponse>(`${DEALER_API_URL}/event/create-request`, {
      method: 'POST',
      body: JSON.stringify(formattedRequest),
    });
  },

  /**
   * Submit event for PHQ review.
   * POST v1/event/event_request/submit/{id}
   */
  async submitRequest(eventRequestId: number): Promise<ApiResponse<EventResponse>> {
    return doFetch<EventResponse>(
      `${DEALER_API_URL}/event/event_request/submit/${eventRequestId}`,
      {
        method: 'POST',
      }
    );
  },

  /**
   * Save event plan with file upload.
   * POST v1/event/event_request/{id}
   */
  async saveEventPlan(eventPlan: Record<string, unknown>): Promise<ApiResponse<EventResponse>> {
    const formData = new FormData();

    // Add all fields to FormData, excluding edit_log
    for (const [key, value] of Object.entries(eventPlan)) {
      if (key !== 'edit_log' && value !== null && value !== undefined) {
        if (value instanceof File) {
          formData.append(key, value);
        } else {
          formData.append(key, String(value));
        }
      }
    }

    return doMultipart<EventResponse>(
      `${DEALER_API_URL}/event/event_request/${eventPlan.event_request_id}`,
      formData
    );
  },

  /**
   * Save reimbursement request with file upload.
   * POST v1/event/event_reimbursement/{id}
   */
  async saveReimbursement(
    reimbursementInfo: Record<string, unknown>
  ): Promise<ApiResponse<unknown>> {
    const formData = new FormData();

    for (const [key, value] of Object.entries(reimbursementInfo)) {
      if (key !== 'edit_log' && value !== null && value !== undefined) {
        if (value instanceof File) {
          formData.append(key, value);
        } else {
          formData.append(key, String(value));
        }
      }
    }

    return doMultipart(
      `${DEALER_API_URL}/event/event_reimbursement/${reimbursementInfo.event_id}`,
      formData
    );
  },

  // --------------------------------------------------------------------------
  // Event Support
  // --------------------------------------------------------------------------

  /**
   * Create magic link token for event portal access.
   * POST v1/auth/create_login_token
   */
  async getEventToken(eventId: string): Promise<ApiResponse<EventTokenResponse>> {
    return doFetch<EventTokenResponse>(`${DEALER_API_URL}/auth/create_login_token`, {
      method: 'POST',
      body: JSON.stringify({ event_id: eventId }),
    });
  },

  /**
   * Get survey respondents for event.
   * GET v1/event/list-respondents/{id}
   */
  async getRespondents(eventId: string): Promise<ApiResponse<RespondentsResponse>> {
    return doFetch<RespondentsResponse>(`${DEALER_API_URL}/event/list-respondents/${eventId}`, {
      method: 'GET',
    });
  },

  /**
   * Send 2FA verification code via email.
   * POST v1/event/send-2fa-email/{email}
   */
  async send2FAEmail(contactEmail: string): Promise<ApiResponse<TwoFAResponse>> {
    return doFetch<TwoFAResponse>(`${DEALER_API_URL}/event/send-2fa-email/${contactEmail}`, {
      method: 'POST',
    });
  },

  // --------------------------------------------------------------------------
  // Events/Survey API
  // --------------------------------------------------------------------------

  /**
   * Get survey configuration by hash.
   * GET survey/config/{hash}
   */
  async getSurveyConfig(hash: string): Promise<ApiResponse<SurveyConfigResponse>> {
    return doFetch<SurveyConfigResponse>(`${EVENTS_API_URL}/survey/config/${hash}`, {
      method: 'GET',
    });
  },

  /**
   * Submit survey response.
   * POST survey/insert/v1
   */
  async submitSurvey(
    data: SurveyResponse,
    accessKey: string
  ): Promise<ApiResponse<SurveySubmitResponse>> {
    return doFetch<SurveySubmitResponse>(`${EVENTS_API_URL}/survey/insert/v1`, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { authorization: accessKey },
    });
  },

  /**
   * Submit vehicles of interest.
   * POST survey/insert/vehicles/interested
   */
  async submitVehiclesOfInterest(
    data: VehicleOfInterest[],
    accessKey: string
  ): Promise<ApiResponse<{ success: boolean }>> {
    return doFetch(`${EVENTS_API_URL}/survey/insert/vehicles/interested`, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { authorization: accessKey },
    });
  },

  /**
   * Submit vehicles driven.
   * POST survey/insert/vehicles/driven
   */
  async submitVehiclesDriven(
    data: { participant_id: number; vehicle_ids: number[] },
    accessKey: string
  ): Promise<ApiResponse<{ success: boolean }>> {
    return doFetch(`${EVENTS_API_URL}/survey/insert/vehicles/driven`, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { authorization: accessKey },
    });
  },

  // --------------------------------------------------------------------------
  // Check-in/out API
  // --------------------------------------------------------------------------

  /**
   * Get check-in data for event.
   * POST check/in/data
   */
  async getCheckInData(eventId: number): Promise<ApiResponse<CheckInDataResponse>> {
    return doFetch<CheckInDataResponse>(`${MOBILE_API_URL}/check/in/data`, {
      method: 'POST',
      body: JSON.stringify({ event_id: eventId }),
    });
  },

  /**
   * Check in a participant.
   * POST check/in
   */
  async checkIn(data: {
    participant_id: number;
    event_id: number;
  }): Promise<ApiResponse<{ success: boolean }>> {
    return doFetch(`${MOBILE_API_URL}/check/in`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Get check-out data for event.
   * POST check/out/data
   */
  async getCheckOutData(eventId: number): Promise<ApiResponse<CheckOutDataResponse>> {
    return doFetch<CheckOutDataResponse>(`${MOBILE_API_URL}/check/out/data`, {
      method: 'POST',
      body: JSON.stringify({ event_id: eventId }),
    });
  },

  /**
   * Check out a participant.
   * POST check/out
   */
  async checkOut(data: CheckOutRecord): Promise<ApiResponse<{ success: boolean }>> {
    return doFetch(`${MOBILE_API_URL}/check/out`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // --------------------------------------------------------------------------
  // Stats API
  // --------------------------------------------------------------------------

  /**
   * Get event statistics.
   * GET v1/event/stats/{id}
   */
  async getEventStats(eventId: number): Promise<ApiResponse<EventStatsResponse>> {
    return doFetch<EventStatsResponse>(`${MOBILE_API_URL}/event/stats/${eventId}`, {
      method: 'GET',
    });
  },

  // --------------------------------------------------------------------------
  // Training API
  // --------------------------------------------------------------------------

  /**
   * Get training progress.
   * GET v1/training/progress/{eventId}
   */
  async getTrainingProgress(eventId: number): Promise<ApiResponse<TrainingProgressResponse>> {
    return doFetch<TrainingProgressResponse>(`${MOBILE_API_URL}/training/progress/${eventId}`, {
      method: 'GET',
    });
  },

  /**
   * Mark training section complete.
   * POST v1/training/complete
   */
  async markTrainingComplete(data: {
    event_id: number;
    section: string;
  }): Promise<ApiResponse<{ success: boolean }>> {
    return doFetch(`${EVENTS_API_URL}/training/complete`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // --------------------------------------------------------------------------
  // Utility API
  // --------------------------------------------------------------------------

  /**
   * Convert hash to auth token.
   * POST auth/hash_to_token
   */
  async hashToToken(hash: string): Promise<ApiResponse<HashToTokenResponse>> {
    return doFetch<HashToTokenResponse>(`${MOBILE_API_URL}/auth/hash_to_token`, {
      method: 'POST',
      body: JSON.stringify({ hash }),
    });
  },

  /**
   * Auto-login with token.
   * POST auth/auto_login
   */
  async autoLogin(token: string): Promise<ApiResponse<MagicLoginResponse>> {
    return doFetch<MagicLoginResponse>(`${MOBILE_API_URL}/auth/auto_login`, {
      method: 'POST',
      headers: { authorization: token },
    });
  },

  /**
   * Generate QR code for event.
   * GET v1/event/qrcode/{hashId}
   */
  async generateQRCode(hashId: string): Promise<ApiResponse<{ url: string }>> {
    return doFetch(`${MOBILE_API_URL}/event/qrcode/${hashId}`, {
      method: 'GET',
    });
  },

  /**
   * Generate flyer for event.
   * GET v1/event/flyer/{eventId}
   */
  async generateFlyer(
    eventId: number,
    language: string = 'en'
  ): Promise<ApiResponse<{ url: string }>> {
    return doFetch(`${MOBILE_API_URL}/event/flyer/${eventId}?lang=${language}`, {
      method: 'GET',
    });
  },
};
